import React from 'react'
import {Form, Dropdown, Input, Checkbox, Radio} from 'semantic-ui-react'
import moment from 'moment'
import {
  FREQUENCY_OPTIONS,
  POSITION_OPTIONS,
  WEEKDAY_NAMES,
  WEEKDAY_SHORT,
  applyPreset,
  defaultRecurrence,
  describeRecurrence
} from '../../../../../utils/recurrence'

const {Field} = Form

const PRESET_OPTIONS = [
  {key: 'daily', text: 'Daily', value: 'daily'},
  {key: 'weekdays', text: 'Every weekday (Mon–Fri)', value: 'weekdays'},
  {key: 'weekly', text: 'Weekly on start day', value: 'weekly'},
  {key: 'fortnightly', text: 'Every 2 weeks', value: 'fortnightly'},
  {key: 'monthly', text: 'Monthly', value: 'monthly'},
  {key: 'every_2_months', text: 'Every 2 months', value: 'every_2_months'},
  {key: 'every_3_months', text: 'Every 3 months', value: 'every_3_months'},
  {key: 'every_6_months', text: 'Every 6 months', value: 'every_6_months'},
  {key: 'yearly', text: 'Yearly', value: 'yearly'},
  {key: 'custom', text: 'Custom...', value: 'custom'}
]

class RecurrencePicker extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      preset: props.editMode ? 'custom' : 'weekly',
      showCustom: !!props.editMode
    }
  }

  componentDidMount () {
    if (!this.props.value || !this.props.value.frequency) {
      this.props.onChange(defaultRecurrence(this.props.startDate))
    }
  }

  onPresetChange (e, {value}) {
    if (value === 'custom') {
      this.setState({preset: value, showCustom: true})
      return
    }
    const recurrence = applyPreset(value, this.props.startDate)
    this.setState({preset: value, showCustom: false})
    this.props.onChange(recurrence)
  }

  updateRecurrence (changes) {
    this.setState({preset: 'custom', showCustom: true})
    this.props.onChange(Object.assign({}, this.props.value, changes))
  }

  onIntervalChange (e, {value}) {
    const interval = Math.max(1, parseInt(value, 10) || 1)
    this.updateRecurrence({interval})
  }

  onFrequencyChange (e, {value}) {
    const recurrence = Object.assign({}, this.props.value, {frequency: value})
    if (value === 'weekly' && (!recurrence.days_of_week || !recurrence.days_of_week.length)) {
      recurrence.days_of_week = [moment(this.props.startDate).day()]
    }
    if (value === 'monthly' && !recurrence.monthly_type) {
      recurrence.monthly_type = 'day_of_month'
      recurrence.day_of_month = moment(this.props.startDate).date()
    }
    this.updateRecurrence(recurrence)
  }

  onWeekdayToggle (day) {
    const current = (this.props.value && this.props.value.days_of_week) || []
    let next
    if (current.includes(day)) {
      next = current.filter(d => d !== day)
    } else {
      next = current.concat(day).sort((a, b) => a - b)
    }
    if (next.length === 0) {
      next = [day]
    }
    this.updateRecurrence({days_of_week: next})
  }

  onMonthlyTypeChange (e, {value}) {
    this.updateRecurrence({monthly_type: value})
  }

  onDayOfMonthChange (e, {value}) {
    this.updateRecurrence({day_of_month: Math.min(31, Math.max(1, parseInt(value, 10) || 1))})
  }

  onWeekdayPositionChange (e, {value}) {
    this.updateRecurrence({weekday_position: value})
  }

  onMonthlyWeekdayChange (e, {value}) {
    this.updateRecurrence({weekday: parseInt(value, 10)})
  }

  renderWeekdaySelector () {
    const selected = (this.props.value && this.props.value.days_of_week) || []
    return (
      <Field>
        <label>On</label>
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '12px'}}>
          {WEEKDAY_SHORT.map((label, index) => (
            <Checkbox
              key={label}
              label={label}
              checked={selected.includes(index)}
              onChange={() => this.onWeekdayToggle(index)}
            />
          ))}
        </div>
      </Field>
    )
  }

  renderMonthlyOptions () {
    const recurrence = this.props.value || {}
    const monthlyType = recurrence.monthly_type || 'day_of_month'
    const dayOfMonth = recurrence.day_of_month || moment(this.props.startDate).date()
    const weekdayOptions = WEEKDAY_NAMES.map((name, index) => ({
      key: name,
      text: name,
      value: index
    }))
    const inlineRowStyle = {display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}

    return (
      <div>
        <Field>
          <div style={inlineRowStyle}>
            <Radio
              name='monthlyType'
              value='day_of_month'
              checked={monthlyType === 'day_of_month'}
              onChange={this.onMonthlyTypeChange.bind(this)}
            />
            <span>On day</span>
            <Input
              type='number'
              min={1}
              max={31}
              value={dayOfMonth}
              disabled={monthlyType !== 'day_of_month'}
              onChange={this.onDayOfMonthChange.bind(this)}
              style={{width: '70px'}}
            />
            <span>of every month</span>
          </div>
        </Field>
        <Field>
          <div style={inlineRowStyle}>
            <Radio
              name='monthlyType'
              value='day_of_week'
              checked={monthlyType === 'day_of_week'}
              onChange={this.onMonthlyTypeChange.bind(this)}
            />
            <span>On the</span>
            <Dropdown
              selection
              compact
              disabled={monthlyType !== 'day_of_week'}
              options={POSITION_OPTIONS}
              value={recurrence.weekday_position || 'first'}
              onChange={this.onWeekdayPositionChange.bind(this)}
            />
            <Dropdown
              selection
              compact
              disabled={monthlyType !== 'day_of_week'}
              options={weekdayOptions}
              value={recurrence.weekday != null ? recurrence.weekday : moment(this.props.startDate).day()}
              onChange={this.onMonthlyWeekdayChange.bind(this)}
            />
            <span>of every month</span>
          </div>
        </Field>
      </div>
    )
  }

  render () {
    const recurrence = this.props.value || defaultRecurrence(this.props.startDate)
    const showCustom = this.state.showCustom || this.state.preset === 'custom'
    const summary = describeRecurrence(recurrence, this.props.startDate)

    return (
      <div>
        <Field>
          <label>Repeat</label>
          <Dropdown
            fluid
            selection
            placeholder='Choose a repeat pattern'
            options={PRESET_OPTIONS}
            value={this.state.preset}
            onChange={this.onPresetChange.bind(this)}
          />
        </Field>

        {summary ? (
          <p style={{color: '#666', marginBottom: '1em'}}>{summary}</p>
        ) : null}

        {showCustom ? (
          <div style={{border: '1px solid #e8e8e8', borderRadius: '4px', padding: '1em', marginBottom: '1em'}}>
            <Field>
              <label>Repeat every</label>
              <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                <Input
                  type='number'
                  min={1}
                  value={recurrence.interval || 1}
                  onChange={this.onIntervalChange.bind(this)}
                  style={{width: '80px'}}
                />
                <Dropdown
                  selection
                  options={FREQUENCY_OPTIONS}
                  value={recurrence.frequency || 'weekly'}
                  onChange={this.onFrequencyChange.bind(this)}
                />
              </div>
            </Field>

            {recurrence.frequency === 'weekly' ? this.renderWeekdaySelector() : null}
            {recurrence.frequency === 'monthly' ? this.renderMonthlyOptions() : null}
          </div>
        ) : null}
      </div>
    )
  }
}

export default RecurrencePicker



// WEBPACK FOOTER //
// ./src/pages/issues/overview/components/new/RecurrencePicker.js